/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD && Cyprien DE LA POEZE D HARAMBURE
 * Copyright (c) 2025 Prodeo - All Rights Reserved
 */

/* ===== Required import ===== */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dispatch } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { dayjs, FORMAT_DATE_API, RootState, Site, UUID, AXIOS, CommonGlobalService, ArchiveBoxIcon, DownloadIcon, configService, User, UserIcon, CalendarIcon, CheckIcon, GroupedOption, usePersistantState, LoggerService, CommonSectorService } from '@umanforge/common';

import { API } from 'constants/api.constant';
import { KEY } from 'constants/local_storage_key.constant';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';

import { Note } from 'interfaces/note.interface';
import { Theme } from 'interfaces/theme.interface';
import { EventData } from 'interfaces/eventData.interface';
import { Setting } from 'interfaces/fieldSetting.interface';
import { Engagement } from 'interfaces/engagement.interface';
import { EventDocument } from 'interfaces/document.interface';
import { AdminTableWithSitesApiResponse, EventManager } from 'interfaces/eventManager.interface';
import { Event, EventCommitment, PostEventCommitment, PutEventCommitment } from 'interfaces/types.interface';

import { ToastService } from 'services/toast.service';
import { EventService } from 'services/events.service';
import { ThemeService } from 'services/themes.service';
import { GlobalService } from 'services/global.service';
import { EngagementService } from 'services/engagements.service';
import { FieldSettingService } from 'services/fieldSetting.service';
import { EventManagerService } from 'services/eventManagers.service';
import { VisualisationService } from 'services/visualisation.service';
import { ResolutionPlanData, ResolutionService } from 'services/resolution.service';

import NotesSection from '../events/notesSection.component';
import DocumentsSection from '../events/documentsSection.component';
import BaseModal from '@common_components/modals/baseModal.component';
import EngagementsSection from '../events/engagementsSection.component';
import Tabs, { TabItem } from '@common_components/buttons/Tabs.component';
import BottomModal from '@common_components/modals/bottomModal.component';
import EventCompletionModal from '../events/eventCompletionModal.component';

import { PlusIcon } from '@common_components/icons/plus.icon';
import { EditBoxIcon } from '@common_components/icons/editBox.icon';


interface EventModalProps {
  creationOfEvent?: boolean;
  onEventManagerCreate?: ( event: Event ) => void;
  onClose: () => void;
  isViewMode?: boolean;
  sites?: Site[];
  userClickedId?: string;
  dateCLicked?: string | Date;
  engagementClicked?: EventCommitment;
  isAdminOfEventManager?: boolean;
}

const toastService: ToastService = new ToastService( 'Engagement Modal' );
const logger = new LoggerService( 'EngagementModalComponent' );

const createEventUpdatePayload = (
  eventName: string | undefined,
  themesSelected: string | undefined,
  isHighlighted: boolean | undefined,
  isRecurrent: boolean | undefined,
  referenceEventId: string | undefined,
  what: string | undefined,
  when: string | undefined,
  who: string | undefined,
  where: string | undefined,
  why: string | undefined
) => {
  return {
    title: eventName,
    themeId: themesSelected || '',
    highlighted: isHighlighted,
    recurring: isRecurrent,
    referenceEventId: referenceEventId,
    w5What: what,
    w5When: when,
    w5Who: who,
    w5Where: where,
    w5Why: why
  };
};

const updateEventAPI = async (
  eventId: string,
  eventPayload: {
    title?: string;
    themeId?: string;
    highlighted?: boolean;
    recurring?: boolean;
    referenceEventId?: string;
    w5What?: string;
    w5When?: string;
    w5Who?: string;
    w5Where?: string;
    w5Why?: string;
  },
  token: string | null,
  dispatch: Dispatch
) => {
  if ( !token ) {
    throw new Error( 'Token is required' );
  }

  const response = await AXIOS.put(
    `${API.EVENTS}/${eventId}`,
    { event: eventPayload },
    await CommonGlobalService.getHeaderRequest( token )
  );

  CommonGlobalService.saveRequestToken( response, dispatch );
  return response;
};

const handlePostCreationCleanup = (
  formattedNotes: { value: string; disable: boolean; predecessorId: null }[],
  pendingDocs: File[]
) => {
  const typedWindow = window as WindowWithPending;

  if ( formattedNotes.length > 0 ) {
    typedWindow._pendingNotesForEvent?.clearPendingNotes();
    typedWindow._currentDraftNote = '';
    typedWindow._temporaryNotes = [];
  }

  if ( pendingDocs.length > 0 ) {
    typedWindow._pendingDocumentsForEvent?.clearPendingDocuments();
  }
};

const loadDocumentsAfterCreation = async ( newEventId: string ) => {
  const typedWindow = window as WindowWithPending;

  if ( typedWindow._pendingDocumentsForEvent?.loadDocumentsAfterCreation ) {
    try {
      await typedWindow._pendingDocumentsForEvent.loadDocumentsAfterCreation(
        newEventId
      );
    } catch {
      logger.error( 'Error while loading documents after event creation.' );
    }
  }
};

const EngagementModalLogic = {
  updateEngagement: (
    index: number,
    key: keyof Engagement,
    value: string[] | string | Date,
    setEngagements: React.Dispatch<React.SetStateAction<Engagement[]>>,
    refreshThemes: ( eventManagerIds: string[] ) => Promise<void>
  ) => {
    setEngagements( prevEngagements => {
      const engagementToUpdate = prevEngagements[ index ];

      if ( engagementToUpdate?.archived ) {
        return prevEngagements;
      }

      const updatedEngagements = prevEngagements.map( ( engagement, i ) => {
        return i === index ? { ...engagement, [ key ]: value } : engagement;
      } );

      if ( key === 'eventManagerIds' && Array.isArray( value ) ) {
        const allEventManagers = [ ...new Set(
          updatedEngagements.map( engagement => {
            return engagement.eventManagerIds;
          } ).flat()
        ).values() ];
        if ( allEventManagers.length > 0 ) {
          refreshThemes( allEventManagers );
        }
      }

      return updatedEngagements;
    } );
  },

  addEngagement: (
    setEngagements: React.Dispatch<React.SetStateAction<Engagement[]>>,
    eventManagerId: string,
    daySelected?: string | Date
  ) => {
    setEngagements( prevEngagements => {
      return [
        ...prevEngagements,
        {
          id: '',
          eventManagerIds: [ eventManagerId ],
          participants: [],
          deliveryDate: daySelected ? dayjs( daySelected ).format( FORMAT_DATE_API ) : dayjs().format( FORMAT_DATE_API )
        }
      ];
    } );
  },

  removeEngagement: (
    setEngagements: React.Dispatch<React.SetStateAction<Engagement[]>>,
    index: number,
    toastServiceLocal: ToastService,
    translate: ( key: string ) => string
  ) => {
    setEngagements( prevEngagements => {
      const engagementToRemove = prevEngagements[ index ];

      if ( engagementToRemove?.archived ) {
        toastServiceLocal.showWarning( translate( 'translation:cannot_remove_archived_engagement' ) );
        return prevEngagements;
      }

      return prevEngagements.filter( ( _, idx ) => {
        return idx !== index;
      } );
    } );
  }
};

const useEngagementState = ( engagementClicked?: EventCommitment ) => {
  const [ formValid, setFormValid ] = useState<boolean>( false );
  const [ formChanged, setFormChanged ] = useState<boolean>( false );
  const [ eventName, setEventName ] = useState<string>(
    engagementClicked?.eventTitle || ''
  );
  const [ authorFullName, setAuthorFullName ] = useState<string>(
    engagementClicked?.authorFullName || ''
  );
  const [ creationDate, setCreationDate ] = useState<string>(
    engagementClicked?.creationDate || ''
  );
  const [ themesSelected, setThemeSelected ] = useState<string | null>( null );
  const [ themesOptions, setThemesOptions ] = useState<Theme[]>( [] );
  const [ engagements, setEngagements ] = useState<Engagement[]>( [] );

  return {
    formValid, setFormValid,
    formChanged, setFormChanged,
    eventName, setEventName,
    authorFullName, setAuthorFullName,
    creationDate, setCreationDate,
    themesSelected, setThemeSelected,
    themesOptions, setThemesOptions,
    engagements, setEngagements
  };
};

const use5WState = () => {
  const [ who, setWho ] = useState<string>( '' );
  const [ where, setWhere ] = useState<string>( '' );
  const [ when, setWhen ] = useState<string>( '' );
  const [ what, setWhat ] = useState<string>( '' );
  const [ why, setWhy ] = useState<string>( '' );

  return { who, setWho, where, setWhere, when, setWhen, what, setWhat, why, setWhy };
};

const useModalState = () => {
  const [ isProcessing, setIsProcessing ] = useState<boolean>( false );
  const [ selectedPage, setSelectedPage ] = useState<number>( 0 );
  const [ selectedEngagementIndex, setSelectedEngagementIndex ] = useState<number>( 0 );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isRecurrent, setIsRecurrent ] = useState<boolean>( false );
  const [ isHighlighted, setIsHighlighted ] = useState<boolean>( false );

  return {
    isProcessing, setIsProcessing,
    selectedPage, setSelectedPage,
    selectedEngagementIndex, setSelectedEngagementIndex,
    isFirstRender, setIsFirstRender,
    isRecurrent, setIsRecurrent,
    isHighlighted, setIsHighlighted
  };
};

const useDuplicateParticipants = () => {
  const [ duplicateParticipantsNames, setDuplicateParticipantsNames ] =
    useState<string[]>( [] );

  return { duplicateParticipantsNames, setDuplicateParticipantsNames };
};

const useEventCompletion = () => {
  const [ isEventCompletionModalOpen, setIsEventCompletionModalOpen ] =
    useState<boolean>( false );
  const [ eventToArchive, setEventToArchive ] =
    useState<EventCommitment | null>( null );

  return {
    isEventCompletionModalOpen, setIsEventCompletionModalOpen,
    eventToArchive, setEventToArchive
  };
};

const usePendingStates = () => {
  const [ hasPendingNote, setHasPendingNote ] = useState<boolean>( false );
  const [ hasPendingDocuments, setHasPendingDocuments ] =
    useState<boolean>( false );

  return {
    hasPendingNote, setHasPendingNote,
    hasPendingDocuments, setHasPendingDocuments
  };
};

const useEngagementInitializationEffect = (
  refreshEvent: () => Promise<void>,
  engagementClicked: EventCommitment | undefined,
  setIsHighlighted: React.Dispatch<React.SetStateAction<boolean>>,
  setIsFirstRender: React.Dispatch<React.SetStateAction<boolean>>
) => {
  useEffect( () => {
    const init = async () => {
      await refreshEvent();

      if ( !engagementClicked || ( !engagementClicked.eventTitle && !engagementClicked.eventId ) ) {
        setIsHighlighted( false );
      }

      setIsFirstRender( false );
    };

    init();
  }, [] );
};

const useEngagementSettingsFetchEffect = (
  isFirstRender: boolean,
  selectedSiteId: string | undefined,
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setCustomSettings: React.Dispatch<React.SetStateAction<Setting[]>>,
  token: string | null,
  visualisationToken: { token: string; expirationDate: dayjs.Dayjs; } | null,
  dispatch: Dispatch,
  setDynamicFields: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  isViewMode: boolean,
) => {
  useEffect( () => {
    const fetchSettings = async () => {
      if ( !isFirstRender ) {
        setIsProcessing( true );
        try {
          if ( selectedSiteId != null ) {
            let settings = null;
            if ( isViewMode ) {
              settings = await VisualisationService.getFieldsSettingOfSite( selectedSiteId, visualisationToken, dispatch );
            } else {
              settings = await FieldSettingService.getBySiteId( selectedSiteId, token, dispatch );
            }

            const adapted = ( Array.isArray( settings ) ? settings : [ settings ] )
              .filter( s => {
                const hasValidId = s.id && GlobalService.isValidGuid( s.id );
                return hasValidId;
              } )
              .map( s => {
                const type = s.type === 'string' ? 'text' : s.type === 'int' ? 'number' : s.type;
                let localConstraint = '';
                if ( type === 'text' ) {
                  localConstraint = String( s.textMaxLength || '' );
                } else if ( type === 'number' ) {
                  localConstraint = [ s.numberMin, s.numberMax ].filter( v => {return v !== undefined;} ).join( ',' );
                } else if ( type === 'list' ) {
                  localConstraint = Array.isArray( s.listItems ) ? s.listItems.join( ',' ) : '';
                }

                const adaptedSetting = {
                  name: s.name || '',
                  type,
                  constraint: localConstraint,
                  id: s.id,
                };

                return adaptedSetting;
              } );
            setCustomSettings( adapted );
            setDynamicFields( prev => {
              const next = { ...prev };
              adapted.forEach( s => {
                if ( s.id && !( s.id in next ) ) {
                  next[ s.id ] = '';
                }
              } );
              return next;
            } );

          } else {
            setCustomSettings( [] );
          }
        } catch {
          setCustomSettings( [] );
        } finally {
          setIsProcessing( false );
        }
      }
    };

    fetchSettings();
  }, [ isFirstRender, selectedSiteId ] );
};

const createReferenceEventHandlers = (
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setEventActual: React.Dispatch<React.SetStateAction<EventData | undefined>>,
  setInitialReferenceEventId: React.Dispatch<React.SetStateAction<string | undefined>>,
  setInitialReferenceEventTitle: React.Dispatch<React.SetStateAction<string | undefined>>,
  setEventName: React.Dispatch<React.SetStateAction<string>>,
  setThemeSelected: React.Dispatch<React.SetStateAction<string | null>>,
  setEngagements: React.Dispatch<React.SetStateAction<Engagement[]>>,
  setIsRecurrent: React.Dispatch<React.SetStateAction<boolean>>,
  setWhat: React.Dispatch<React.SetStateAction<string>>,
  setWhen: React.Dispatch<React.SetStateAction<string>>,
  setWho: React.Dispatch<React.SetStateAction<string>>,
  setWhere: React.Dispatch<React.SetStateAction<string>>,
  setWhy: React.Dispatch<React.SetStateAction<string>>,
  setDynamicFieldsActual: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setDynamicFields: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  eventName: string,
  themesSelected: string | null,
  engagements: Engagement[],
  isRecurrent: boolean,
  what: string,
  when: string,
  who: string,
  where: string,
  why: string,
  dynamicFields: Record<string, string>,
  token: string | null,
  visualisationToken: { token: string; expirationDate: dayjs.Dayjs; } | null,
  dispatch: Dispatch,
  toastServiceLocal: ToastService,
  t: ( key: string ) => string,
  isViewMode: boolean | undefined
) => {
  const loadReferenceEventForCreation = async ( referenceEventId: string ) => {
    setIsProcessing( true );
    try {
      const referenceEvent = await EventService.getEventById(
        referenceEventId,
        token,
        dispatch
      );


      if ( referenceEvent && referenceEvent.event ) {
        setEventActual( {
          event: {
            id: '',
            title: '',
            themeId: '',
            authorId: '',
            highlighted: false,
            recurring: false,
            referenceEventId: referenceEvent.event.id,
            referenceEventTitle: referenceEvent.event.title,
            w5What: '',
            w5When: '',
            w5Who: '',
            w5Where: '',
            w5Why: '',
            authorFullname: referenceEvent.event.authorFullname,
            createdDate: referenceEvent.event.createdDate
          },
          engagements: [],
          notes: [],
          documents: []
        } );

        setInitialReferenceEventId( referenceEvent.event.id );
        setInitialReferenceEventTitle( referenceEvent.event.title );
        if ( !eventName || eventName.trim() === '' ) {
          setEventName( referenceEvent.event.title );
        }
        if ( !themesSelected || themesSelected === '' ) {
          setThemeSelected( referenceEvent.event.themeId );
        }
        if ( engagements.length === 0 || engagements.every( e => {return e.participants.length === 0;} ) ) {
          setEngagements( referenceEvent.engagements );
        }
        if ( !isRecurrent ) {
          setIsRecurrent( Boolean( referenceEvent.event.recurring ) );
        }

        if ( !what || what.trim() === '' ) {
          if ( referenceEvent.event.w5What ) {
            setWhat( referenceEvent.event.w5What );
          }
        }
        if ( !when || when.trim() === '' ) {
          if ( referenceEvent.event.w5When ) {
            setWhen( referenceEvent.event.w5When );
          }
        }
        if ( !who || who.trim() === '' ) {
          if ( referenceEvent.event.w5Who ) {
            setWho( referenceEvent.event.w5Who );
          }
        }
        if ( !where || where.trim() === '' ) {
          if ( referenceEvent.event.w5Where ) {
            setWhere( referenceEvent.event.w5Where );
          }
        }
        if ( !why || why.trim() === '' ) {
          if ( referenceEvent.event.w5Why ) {
            setWhy( referenceEvent.event.w5Why );
          }
        }

        const fieldValues = await FieldSettingService.getFieldValuesByEventId( referenceEvent.event.id!, token, dispatch );
        if ( fieldValues && Array.isArray( fieldValues ) ) {
          const fieldsMap: Record<string, string> = {};
          ( fieldValues as { settingId?: string; value?: unknown }[] ).forEach( fieldValue => {
            if ( fieldValue.settingId && fieldValue.value !== undefined ) {
              const currentValue = dynamicFields[ fieldValue.settingId ];
              if ( !currentValue || currentValue.trim() === '' ) {
                fieldsMap[ fieldValue.settingId ] = String( fieldValue.value );
              } else {
                fieldsMap[ fieldValue.settingId ] = currentValue;
              }
            }
          } );
          setDynamicFieldsActual( fieldsMap );
          setDynamicFields( fieldsMap );
        } else if ( fieldValues && typeof fieldValues === 'object' ) {
          const updatedFields = { ...dynamicFields };
          Object.entries( fieldValues as Record<string, string> ).forEach( ( [ key, value ] ) => {
            if ( !updatedFields[ key ] || updatedFields[ key ].trim() === '' ) {
              updatedFields[ key ] = value;
            }
          } );
          setDynamicFieldsActual( updatedFields );
          setDynamicFields( updatedFields );
        }

        toastServiceLocal.showSuccess( t( 'translation:reference_event_loaded_successfully' ) );
      } else {
        toastServiceLocal.showWarning( t( 'translation:reference_event_not_found' ) );
      }
    } catch {
      toastServiceLocal.showError( t( 'translation:error_loading_reference_event' ) );
    } finally {
      setIsProcessing( false );
    }
  };

  const linkReferenceEventForModification = async ( referenceEventId: string ) => {
    setIsProcessing( true );
    try {
      let referenceEvent: EventData | null = null;
      if ( isViewMode ) {
        referenceEvent = await VisualisationService.getEventById( referenceEventId, visualisationToken, dispatch );
      } else {
        referenceEvent = await EventService.getEventById( referenceEventId, token, dispatch );
      }

      if ( referenceEvent && referenceEvent.event ) {
        setEventActual( prev => {
          if ( !prev ) {return prev;}
          return {
            ...prev,
            event: {
              ...prev.event,
              referenceEventId: referenceEvent.event.id,
              referenceEventTitle: referenceEvent.event.title
            }
          };
        } );
      } else {
        toastServiceLocal.showWarning( t( 'translation:reference_event_not_found' ) );
      }
    } catch {
      toastServiceLocal.showError( t( 'translation:error_linking_reference_event' ) );
    } finally {
      setIsProcessing( false );
    }
  };

  return {
    loadReferenceEventForCreation,
    linkReferenceEventForModification
  };
};

const createModalActionHandlers = (
  engagementClicked: EventCommitment | undefined,
  isProcessing: boolean,
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setEventToArchive: React.Dispatch<React.SetStateAction<EventCommitment | null>>,
  setIsEventCompletionModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  token: string | null,
  dispatch: Dispatch,
  toastServiceLocal: ToastService,
  t: ( key: string ) => string,
  onClose: () => void,
  selectedSectorId: string | undefined,
  connectedUser: User | null,
  eventActual: EventData | undefined,
  who: string,
  what: string,
  where: string,
  when: string,
  language?: string
) => {
  const handleArchived = async () => {
    if ( isProcessing ) {
      return;
    }

    if ( engagementClicked && engagementClicked.id ) {
      if ( !engagementClicked.archived && !engagementClicked.completed ) {
        setEventToArchive( engagementClicked );
        setIsEventCompletionModalOpen( true );
        return;
      }

      setIsProcessing( true );

      const body = {
        archived: !engagementClicked.archived
      };

      try {
        await EngagementService.updateEngagementAPI( engagementClicked.id, body, token, dispatch );
        toastServiceLocal.showSuccess(
          engagementClicked.archived
            ? t( 'translation:engagement_unarchived' )
            : t( 'translation:engagement_archived' )
        );
      } catch ( error ) {
        logger.error( 'Error updating engagement archive status:', error );
        toastServiceLocal.showError( t( 'translation:error_updating_engagement' ) );
      } finally {
        setIsProcessing( false );
        onClose();
      }
    }
  };

  const handleCompleted = async () => {
    if ( isProcessing ) {
      return;
    }

    if ( engagementClicked && engagementClicked.id ) {
      setIsProcessing( true );

      const body = {
        completed: !engagementClicked.completed
      };

      try {
        await EngagementService.updateEngagementAPI( engagementClicked.id, body, token, dispatch );
        toastServiceLocal.showSuccess(
          engagementClicked.completed
            ? t( 'translation:engagement_marked_not_completed' )
            : t( 'translation:engagement_marked_completed' )
        );
      } catch ( error ) {
        logger.error( 'Error updating engagement completion status:', error );
        toastServiceLocal.showError( t( 'translation:error_updating_engagement' ) );
      } finally {
        setIsProcessing( false );
        onClose();
      }
    }
  };

  const handleLaunchResolution = async () => {
    if ( !engagementClicked?.eventId || !eventActual?.event?.id ) {
      toastServiceLocal.showError( t( 'translation:resolution_no_event_selected' ) );
      return;
    }

    if ( !selectedSectorId ) {
      toastServiceLocal.showError( t( 'translation:resolution_no_sector_selected' ) );
      return;
    }

    if ( !connectedUser?.id ) {
      toastServiceLocal.showError( t( 'translation:resolution_no_user_connected' ) );
      return;
    }

    try {
      setIsProcessing( true );
      const sector = await CommonSectorService.getSector( selectedSectorId, token, dispatch );

      const resolutionData: ResolutionPlanData = {
        title: eventActual.event.title || t( 'translation:resolution_default_title' ),
        pilote: {
          id: connectedUser.id,
          firstname: connectedUser.firstname,
          lastname: connectedUser.lastname,
          email: connectedUser.email
        },
        site: {
          id: sector.id,
          name: sector.name
        },
        flashMeetingId: eventActual.event.id,
        tool6w: {
          descWho: who || '',
          descWhat: what || '',
          descWhere: where || '',
          descWhen: when || '',
        }
      };

      const result = await ResolutionService.createResolutionPlan(
        connectedUser.id,
        resolutionData,
        token,
        dispatch
      );

      if ( result && result.id ) {
        window.open( await configService.getResolutionUrl() + '#/rp/' + result.id, '_blank' )?.focus();
        toastServiceLocal.showSuccess( t( 'translation:resolution_launched_successfully' ) );
        onClose();
      } else {
        toastServiceLocal.showError( t( 'translation:resolution_launch_failed' ) );
      }
    } catch ( error ) {
      logger.error( 'Error launching resolution:', error );
      toastServiceLocal.showError( t( 'translation:resolution_launch_error' ) );
    } finally {
      setIsProcessing( false );
    }
  };

  const handleDownloadPDF = async () => {
    if ( !eventActual?.event?.id ) {
      toastServiceLocal.showError( t( 'translation:error_downloading_pdf' ) );
      return;
    }

    if ( isProcessing ) {
      return;
    }

    try {
      setIsProcessing( true );

      const blob = await EventService.downloadTicketPDF(
        eventActual.event.id,
        selectedSectorId,
        language,
        token,
        dispatch
      );

      // Create blob download
      const url = window.URL.createObjectURL( blob );
      const link = document.createElement( 'a' );
      link.href = url;
      link.download = `event_${eventActual.event.id}_${eventActual.event.title?.replace( /\s+/g, '_' ) || 'ticket'}.pdf`;
      document.body.appendChild( link );
      link.click();
      document.body.removeChild( link );
      window.URL.revokeObjectURL( url );

      toastServiceLocal.showSuccess( t( 'translation:pdf_downloaded_successfully' ) );
    } catch ( error ) {
      logger.error( 'Error downloading PDF:', error );
      toastServiceLocal.showError( t( 'translation:error_downloading_pdf' ) );
    } finally {
      setIsProcessing( false );
    }
  };

  return {
    handleArchived,
    handleCompleted,
    handleLaunchResolution,
    handleDownloadPDF
  };
};

const createValidationHelpers = (
  eventName: string,
  themesSelected: string | null,
  engagements: Engagement[],
  eventActual: EventData | undefined,
  isRecurrent: boolean,
  isHighlighted: boolean,
  who: string,
  where: string,
  when: string,
  what: string,
  why: string,
  customSettings: Setting[],
  dynamicFields: Record<string, string>,
  dynamicFieldsActual: Record<string, string>,
  initialReferenceEventId: string | undefined,
  initialReferenceEventTitle: string | undefined,
  t: ( key: string ) => string,
  token: string | null,
  dispatch: Dispatch
) => {
  const validateFormRequirements = (): boolean => {
    return (
      eventName != null && eventName != '' &&
      themesSelected != null && themesSelected != '' &&
      engagements.every( engagement => {
        return engagement.participants.length != 0 &&
               engagement.eventManagerIds.length != 0 &&
               engagement.deliveryDate != '';
      } )
    );
  };

  const checkFormChanges = (): boolean => {
    const isEventNameChange = eventActual == null ||
      eventName != eventActual.event.title;

    const isEventThemeChange = eventActual == null ||
      themesSelected != eventActual.event.themeId;

    const isRecurrentChange = eventActual == null ||
      isRecurrent != eventActual.event.recurring;

    const isHighlightedChange = eventActual == null ||
      isHighlighted != eventActual.event.highlighted;

    return isEventNameChange || isEventThemeChange ||
           isRecurrentChange || isHighlightedChange ||
           checkEngagementChanges() || check5WChanges() ||
           checkDynamicFieldChanges() || checkReferenceEventChanges();
  };

  const checkEngagementChanges = (): boolean => {
    if ( eventActual == null ) {
      return true;
    }

    if ( engagements.length != eventActual.engagements.length ) {
      return true;
    }

    return engagements.some( engagement => {
      const actualEngagement = eventActual.engagements.find(
        item => {
          return item.id === engagement.id;
        }
      );
      if ( !actualEngagement ) {
        return true;
      }

      const participantsChanged =
        engagement.participants.sort().join() !=
        actualEngagement.participants.sort().join();

      const managersChanged =
        engagement.eventManagerIds.sort().join() !=
        actualEngagement.eventManagerIds.sort().join();

      const dateChanged = !dayjs( engagement.deliveryDate )
        .startOf( 'day' )
        .isSame( dayjs( actualEngagement.deliveryDate ).startOf( 'day' ) );

      return participantsChanged || managersChanged || dateChanged;
    } );
  };

  const check5WChanges = (): boolean => {
    return eventActual == null || (
      who != ( eventActual.event.w5Who ?? '' ) ||
      where != ( eventActual.event.w5Where ?? '' ) ||
      when != ( eventActual.event.w5When ?? '' ) ||
      what != ( eventActual.event.w5What ?? '' ) ||
      why != ( eventActual.event.w5Why ?? '' )
    );
  };

  const checkDynamicFieldChanges = (): boolean => {
    return eventActual == null || (
      customSettings.some( setting => {
        if ( setting.id ) {
          const actualValue = dynamicFieldsActual[ setting.id ] ?? '';
          const currentValue = dynamicFields[ setting.id ] ?? '';
          return actualValue != currentValue;
        }
        return false;
      } )
    );
  };

  const checkReferenceEventChanges = (): boolean => {
    return eventActual == null || (
      ( initialReferenceEventId ?? '' ) !=
      ( eventActual.event.referenceEventId ?? '' ) ||
      ( initialReferenceEventTitle ?? '' ) !=
      ( eventActual.event.referenceEventTitle ?? '' )
    );
  };

  const checkIsValidEngagements = async ( engagementsToCheck: Engagement[] ): Promise<string | null> => {
    const errorMessageDuplication = checkIsDuplicatedEngagements( engagementsToCheck );
    const errorMessageDate = await checkIsValidDateEngagements( engagementsToCheck );

    return errorMessageDuplication || errorMessageDate;
  };

  const checkIsValidDateEngagements = async ( engagementsToCheck: Engagement[] ): Promise<string | null> => {

    const promises = engagementsToCheck.map( engagement => {
      return new Promise<string | null>( resolve => {
        if ( engagement.eventManagerIds.length === 0 || engagement.participants.length === 0 ) {
          resolve( null );
        } else {
          EngagementService.checkEngagementDateAvailability(
            engagement.eventManagerIds,
            engagement.participants,
            dayjs( engagement.deliveryDate ),
            token,
            dispatch
          )
            .then( response => {
              resolve( response.errorMessage === '' ? null : response.errorMessage );
            } );
        }
      } );
    } );

    const errorMessages = ( await Promise.all( promises ) ).filter( errorMessage => { return errorMessage != null;  } );
    return errorMessages.length > 0 ? errorMessages[ 0 ] : null;
  };

  const checkIsDuplicatedEngagements = ( engagementsToCheck: Engagement[] ): string | null => {
    const participantsMap: Record<string, number> = {};
    const duplicateEngagement: string[] = [];
    engagementsToCheck.forEach( engagement => {
      for ( const eventManagerIdOfEngagement of engagement.eventManagerIds ) {
        for ( const participantIdOfEngagement of engagement.participants ) {
          const engagementKey = `${engagement.deliveryDate}_${eventManagerIdOfEngagement}_${participantIdOfEngagement}`;
          participantsMap[ engagementKey ] = ( participantsMap[ engagementKey ] || 0 ) + 1;
          if ( participantsMap[ engagementKey ] === 2 ) {
            duplicateEngagement.push( participantIdOfEngagement );
          }
        }
      }
    } );

    let errorMessage: string | null = null;
    if ( duplicateEngagement.length > 0 ) {
      errorMessage = t( 'translation:duplicate_participants_error' );
    }

    return errorMessage;
  };

  return {
    validateFormRequirements,
    checkFormChanges,
    checkEngagementChanges,
    check5WChanges,
    checkDynamicFieldChanges,
    checkReferenceEventChanges,
    checkIsValidEngagements
  };
};

const createEngagementHandlers = (
  setEngagements: React.Dispatch<React.SetStateAction<Engagement[]>>,
  eventManagerId: string | undefined,
  toastServiceLocal: ToastService,
  translate: ( key: string ) => string,
  refreshThemes: ( eventManagerIds: string[] ) => Promise<void>,
  daySelectedRaw?: string | Date
) => {

  const updateEngagement = ( index: number, key: keyof Engagement, value: string[] | string | Date ) => {
    EngagementModalLogic.updateEngagement( index, key, value, setEngagements, refreshThemes );
  };

  const addEngagement = () => {
    EngagementModalLogic.addEngagement( setEngagements, eventManagerId ?? '', daySelectedRaw );
  };

  const removeEngagement = ( index: number ) => {
    EngagementModalLogic.removeEngagement( setEngagements, index, toastServiceLocal, translate );
  };

  return { updateEngagement, addEngagement, removeEngagement };
};

const createRefreshFunctions = (
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setEventActual: React.Dispatch<React.SetStateAction<EventData | undefined>>,
  setThemeSelected: React.Dispatch<React.SetStateAction<string | null>>,
  setEngagements: React.Dispatch<React.SetStateAction<Engagement[]>>,
  setIsRecurrent: React.Dispatch<React.SetStateAction<boolean>>,
  setIsHighlighted: React.Dispatch<React.SetStateAction<boolean>>,
  setInitialReferenceEventId: React.Dispatch<React.SetStateAction<string | undefined>>,
  setInitialReferenceEventTitle: React.Dispatch<React.SetStateAction<string | undefined>>,
  setWhat: React.Dispatch<React.SetStateAction<string>>,
  setWhen: React.Dispatch<React.SetStateAction<string>>,
  setWho: React.Dispatch<React.SetStateAction<string>>,
  setWhere: React.Dispatch<React.SetStateAction<string>>,
  setWhy: React.Dispatch<React.SetStateAction<string>>,
  setDynamicFieldsActual: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setDynamicFields: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setEventManagersOptions: React.Dispatch<React.SetStateAction<GroupedOption<{ id: UUID, name: string }>[]>>,
  refreshThemes: ( eventManagerIds: string[] ) => Promise<void>,
  engagementClicked: EventCommitment | undefined,
  eventManagerId: string | undefined,
  daySelectedRaw: string | Date | undefined,
  userIdSelected: string,
  connectedUser: User | null,
  token: string | null,
  visualisationToken: { token: string, expirationDate: dayjs.Dayjs } | null,
  dispatch: Dispatch,
  setSelectedEngagementIndex: React.Dispatch<React.SetStateAction<number>>,
  isViewMode?: boolean,
) => {
  const refreshEvent = async () => {
    setIsProcessing( true );
    const uniqueEventManagerIds: UUID[] = [];
    let eventFetched: EventData | null = null;
    if ( engagementClicked && ( engagementClicked.eventTitle || engagementClicked.eventId ) ) {

      if ( isViewMode ) {
        eventFetched = await VisualisationService.getEventById( engagementClicked.eventId, visualisationToken, dispatch );
      } else {
        eventFetched = await EventService.getEventById( engagementClicked.eventId, token, dispatch );
      }

      if ( eventFetched && eventFetched.engagements ) {
        uniqueEventManagerIds.push( ...Array.from(
          new Set( eventFetched.engagements.flatMap( engagement => {return engagement.eventManagerIds;} ) )
        ) );
      }
    } else {
      uniqueEventManagerIds.push( ...( eventManagerId ? [ eventManagerId ] : [] ) );
      setIsHighlighted( false );
    }

    await refreshEventManagerOptions();
    await refreshThemes( uniqueEventManagerIds );
    if ( eventFetched && eventFetched.event && eventFetched.event.id ) {
      let fieldValues: unknown[] | undefined = [];
      if ( isViewMode ) {
        fieldValues = await VisualisationService.getFieldValuesByEventId( eventFetched.event.id, visualisationToken, dispatch );
      } else {
        fieldValues = await FieldSettingService.getFieldValuesByEventId( eventFetched.event.id, token, dispatch );
      }

      if ( fieldValues && Array.isArray( fieldValues ) ) {
        const fieldsMap: Record<string, string> = {};
        ( fieldValues as { settingId?: string; value?: unknown }[] ).forEach( fieldValue => {
          if ( fieldValue.settingId && fieldValue.value !== undefined ) {
            fieldsMap[ fieldValue.settingId ] = String( fieldValue.value );
          }
        } );
        setDynamicFieldsActual( fieldsMap );
        setDynamicFields( fieldsMap );
      } else if ( fieldValues && typeof fieldValues === 'object' ) {
        setDynamicFieldsActual( fieldValues as Record<string, string> );
        setDynamicFields( fieldValues as Record<string, string> );
      }

      setEventActual( eventFetched );
      setThemeSelected( eventFetched.event.themeId );
      setEngagements( eventFetched.engagements );
      setIsRecurrent( Boolean( eventFetched.event.recurring ) );
      setIsHighlighted( Boolean( eventFetched.event.highlighted ) );
      setInitialReferenceEventId( eventFetched.event.referenceEventId );
      setInitialReferenceEventTitle( eventFetched.event.referenceEventTitle );
      if ( eventFetched.event.w5What ) {
        setWhat( eventFetched.event.w5What );
      }
      if ( eventFetched.event.w5When ) {
        setWhen( eventFetched.event.w5When );
      }
      if ( eventFetched.event.w5Who ) {
        setWho( eventFetched.event.w5Who );
      }
      if ( eventFetched.event.w5Where ) {
        setWhere( eventFetched.event.w5Where );
      }
      if ( eventFetched.event.w5Why ) {
        setWhy( eventFetched.event.w5Why );
      }

      // After setting engagements, if an engagementClicked is provided, select it
      // Note: setEngagements is async in React state, so we set selected page here after
      // the engagements have been determined.
      if ( engagementClicked && engagementClicked.id ) {
        const idx = eventFetched.engagements.findIndex( e => {return e.id === engagementClicked.id;} );
        if ( idx !== -1 ) {
          // schedule selection on next tick to ensure engagements state is applied
          setTimeout( () => { setSelectedEngagementIndex( idx ); }, 0 );
        }
      }

    } else {
      const delivery = daySelectedRaw ? dayjs( daySelectedRaw ).format( FORMAT_DATE_API ) : dayjs().format( FORMAT_DATE_API );
      setEngagements( [
        {
          eventManagerIds: eventManagerId ? [ eventManagerId ] : [],
          participants: [ userIdSelected ],
          deliveryDate: delivery,
        }
      ] );
    }

    if ( !engagementClicked || ( !engagementClicked.eventTitle && !engagementClicked.eventId ) ) {
      setIsHighlighted( false );
    }

    setIsProcessing( false );
  };

  const refreshEventManagerOptions = async (): Promise<void> => {
    try {
      const response: AdminTableWithSitesApiResponse[] = [];
      if ( isViewMode ) {
        response.push( ...( await VisualisationService.getEventManagersWhereParticipantOrAdmin( visualisationToken, dispatch ) ) );
      } else {
        response.push( ...( await EventManagerService.getEventManagersWhereUserIsParticipantOrAdmin( connectedUser!.id, token, dispatch ) ) );
      }

      const groupedOptions: GroupedOption<{ id: UUID, name: string }>[] = [];

      if ( response && Array.isArray( response ) ) {
        const sitesMap = new Map<string, { name: string; eventManagers: { id: string; name: string }[] }>();

        response.forEach( eventManager => {
          if ( eventManager.sites && eventManager.sites.length > 0 ) {
            eventManager.sites.forEach( site => {
              if ( !sitesMap.has( site.id ) ) {
                sitesMap.set( site.id, { name: site.name, eventManagers: [] } );
              }
              sitesMap.get( site.id )?.eventManagers.push( {
                id: eventManager.id,
                name: eventManager.name
              } );
            } );
          }
        } );

        sitesMap.forEach( siteData => {
          if ( siteData.eventManagers.length > 0 ) {
            groupedOptions.push( {
              label: siteData.name,
              options: CommonGlobalService.toOptions( siteData.eventManagers )
            } );
          }
        } );
      }

      setEventManagersOptions( groupedOptions );
    } catch ( error ) {
      logger.error( 'An error occurred while fetching event managers:', error );
      setEventManagersOptions( [] );
    }
  };

  return { refreshEvent, refreshEventManagerOptions };
};

const createUtilityFunctions = (
  dynamicFields: Record<string, string>,
  setDynamicFields: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  eventActual: EventData | undefined,
  engagements: Engagement[],
  token: string | null,
  dispatch: Dispatch,
) => {
  const updateDynamicField = ( fieldId: string, value: string ) => {
    setDynamicFields( prev => {
      const next = { ...prev, [ fieldId ]: value };
      return next;
    } );
  };

  const saveDynamicFieldsToEvent = async ( eventId?: string, allowEmpty: boolean = false ) => {
    const idToUse = eventId || eventActual?.event.id;
    if ( !idToUse ) { return; }

    const typedWindow = ( typeof window !== 'undefined' ? window as WindowWithPending : undefined );
    const pendingFromWindow: Record<string, string> = typedWindow?._pendingFieldSettings || {};
    const mergedFields: Record<string, string> = {
      ...( dynamicFields || {} ),
      ...pendingFromWindow
    };

    const savePromises = Object.entries( mergedFields ).map( async ( [ fieldId, rawStrValue ] ) => {
      if ( !GlobalService.isValidGuid( fieldId ) ) { return; }
      const rawValue: unknown = rawStrValue;

      let shouldPersist = false;
      if ( rawValue === null || rawValue === undefined ) {
        shouldPersist = false;
      } else if ( typeof rawValue === 'string' ) {
        shouldPersist = rawValue.trim() !== '' || allowEmpty;
      } else if ( typeof rawValue === 'number' ) {
        shouldPersist = Number.isFinite( rawValue );
      } else {
        shouldPersist = true;
      }

      if ( shouldPersist ) {
        try {
          await FieldSettingService.addFieldValueToEvent( idToUse, fieldId, String( rawValue ), token ?? null, dispatch );
        } catch ( e ) {
          logger.error( `Error persisting dynamic field ${fieldId}:`, e );
        }
      }
    } );

    await Promise.all( savePromises );

    try { if ( typedWindow ) { typedWindow._pendingFieldSettings = {}; } } catch { /* ignore */ }
  };

  const managedEngagementsOfEvent = async ( eventData: EventData ) => {
    if ( eventData.event.id ) {
      const deleteEng = eventData.engagements
        .filter( actualEngagement => {
          return !engagements.some( engagement => { return actualEngagement.id === engagement.id; } );
        } )
        .map( engagement => { return engagement.id; } )
        .filter( engagementId => {
          return engagementId != null;
        } );

      const putEng = engagements
        .map( engagement => {
          const actualEngagement = eventData.engagements.find( eng => { return eng.id === engagement.id; } );
          if ( actualEngagement && engagement.id != null && engagement.id != '' ) {
            const particiantsId = engagement.participants;
            const eventManagersId = engagement.eventManagerIds;
            const deliveryDate = dayjs( engagement.deliveryDate );

            const isParticiantsChanged = actualEngagement.participants.sort().join() !== particiantsId.sort().join();
            const isEventManagerChanged = actualEngagement.eventManagerIds.sort().join() !== eventManagersId.sort().join();
            const isDeliveryDateChanged = !dayjs( actualEngagement.deliveryDate ).isSame( deliveryDate, 'day' );
            return {
              id: engagement.id,
              participants: isParticiantsChanged ? particiantsId : undefined,
              eventManagerIds: isEventManagerChanged ? eventManagersId : undefined,
              deliveryDate: isDeliveryDateChanged ? deliveryDate.format( FORMAT_DATE_API ) : undefined,
            } as ( PutEventCommitment & { id: UUID } );
          }
        } )
        .filter( engagement => {
          return engagement != undefined;
        } )
        .filter( engagement => {
          return (
            engagement.deliveryDate != undefined ||
            engagement.eventManagerIds != undefined ||
            engagement.participants != undefined
          );
        } );

      const postEng = engagements
        .filter( item => {
          return item.id == null || item.id == '';
        } )
        .map( engagement => {
          return {
            participants: engagement.participants,
            eventManagerIds: engagement.eventManagerIds,
            deliveryDate: dayjs( engagement.deliveryDate ).format( FORMAT_DATE_API )
          } as PostEventCommitment;
        } );

      if ( putEng.length > 0 || postEng.length > 0 || deleteEng.length > 0 ) {
        const success = await EngagementService.managedEngagementAPI( eventData.event.id, deleteEng, putEng, postEng, token, dispatch );
        if ( !success ) {
          logger.error( 'Échec de la mise à jour de des engagements' );
        }
      }
    }

  };

  return { updateDynamicField, saveDynamicFieldsToEvent, managedEngagementsOfEvent };
};

const createEventHandlers = (
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setIsEventCompletionModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setEventToArchive: React.Dispatch<React.SetStateAction<EventCommitment | null>>,
  onClose: () => void,
  token: string | null,
  dispatch: Dispatch,
  toastServiceLocal: ToastService,
  t: ( key: string ) => string
) => {
  const archiveEngagement = async ( eventToArchive: EventCommitment | null, markAsCompleted: boolean = false ) => {
    if ( !eventToArchive?.id ) {
      return;
    }

    setIsProcessing( true );

    const body = {
      archived: true,
      ...( markAsCompleted && { completed: true } )
    };

    try {
      await EngagementService.updateEngagementAPI( eventToArchive.id, body, token, dispatch );
      toastServiceLocal.showSuccess(
        markAsCompleted
          ? t( 'translation:engagement_completed_and_archived' )
          : t( 'translation:engagement_archived' )
      );
    } catch ( error ) {
      logger.error( 'Error archiving engagement:', error );
      toastServiceLocal.showError( t( 'translation:error_archiving_engagement' ) );
    } finally {
      setIsProcessing( false );
      setIsEventCompletionModalOpen( false );
      setEventToArchive( null );
      onClose();
    }
  };

  const handleEventCompletionConfirmNo = async ( eventToArchive: EventCommitment | null ) => {
    await archiveEngagement( eventToArchive, false );
  };

  const handleEventCompletionConfirmYes = async ( eventToArchive: EventCommitment | null ) => {
    await archiveEngagement( eventToArchive, true );
  };

  const handleEventCompletionClose = () => {
    setIsEventCompletionModalOpen( false );
    setEventToArchive( null );
  };

  return {
    archiveEngagement,
    handleEventCompletionConfirmNo,
    handleEventCompletionConfirmYes,
    handleEventCompletionClose
  };
};

const createUseEffectHandlers = (
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  setReferenceEventNotes: React.Dispatch<React.SetStateAction<Note[]>>,
  setReferenceEventDocuments: React.Dispatch<React.SetStateAction<EventDocument[]>>,
  eventActual: EventData | undefined,
  selectedSiteId: string | undefined,
  isFirstRender: boolean,
  token: string | null,
  visualisationToken: { token: string, expirationDate: dayjs.Dayjs } | null,
  dispatch: Dispatch,
  isViewMode?: boolean
) => {
  const loadReferenceNotes = async () => {
    if ( eventActual?.event?.referenceEventId && selectedSiteId ) {
      setIsProcessing( true );
      try {
        const response: Note[] = [];
        if ( isViewMode ) {
          response.push( ...( await VisualisationService.getNotesByEventId( eventActual.event.referenceEventId, selectedSiteId, visualisationToken, dispatch ) ) );
        } else {
          response.push( ...( await EventService.getNotesByEventId( eventActual.event.referenceEventId, selectedSiteId, token, dispatch ) ) );
        }
        setReferenceEventNotes( response );
      } catch {
        setReferenceEventNotes( [] );
      } finally {
        setIsProcessing( false );
      }
    } else {
      setReferenceEventNotes( [] );
    }
  };

  const loadReferenceDocuments = async () => {
    if ( eventActual?.event?.referenceEventId ) {
      setIsProcessing( true );
      try {
        const response: EventDocument[] = [];
        if ( isViewMode ) {
          response.push( ...( await VisualisationService.getDocumentsByEventId( eventActual.event.referenceEventId, visualisationToken, dispatch ) ) );
        } else {
          response.push( ...( await EventService.getDocumentsByEventId( eventActual.event.referenceEventId, token, dispatch ) ) );
        }
        setReferenceEventDocuments( response );
      } catch {
        setReferenceEventDocuments( [] );
      } finally {
        setIsProcessing( false );
      }
    } else {
      setReferenceEventDocuments( [] );
    }
  };

  const useReferenceDataEffect = () => {
    useEffect( () => {
      if ( !isFirstRender && eventActual ) {
        loadReferenceNotes();
        loadReferenceDocuments();
      }
    }, [ eventActual, selectedSiteId, isFirstRender ] );
  };

  return { useReferenceDataEffect };
};

const createParticipantHandlers = (
  engagements: Engagement[],
  setDuplicateParticipantsNames: React.Dispatch<React.SetStateAction<string[]>>,
  token: string | null,
  dispatch: Dispatch,
  t: ( key: string ) => string
) => {
  const getDuplicateParticipantsNames = async ( duplicateIds: string[] ): Promise<string[]> => {
    if ( duplicateIds.length === 0 ) {
      return [];
    }

    try {
      const allEventManagerIds = new Set<string>();
      engagements.forEach( engagement => {
        engagement.eventManagerIds.forEach( id => {return allEventManagerIds.add( id );} );
      } );

      if ( allEventManagerIds.size > 0 ) {
        const users = await EventManagerService.getUsersByEventManagerIds(
          Array.from( allEventManagerIds ),
          token,
          dispatch
        );
        return duplicateIds.map( duplicateId => {
          if ( duplicateId === DEFAULT_BACKLOG.ID ) {
            return t( DEFAULT_BACKLOG.NAME );
          }
          const user = users.find( u => {return u.id === duplicateId;} );
          return user ? `${user.firstname} ${user.lastname}` : t( 'participant_not_found' );
        } );
      }
    } catch ( error ) {
      logger.error( 'Error fetching participant names:', error );
    }

    return duplicateIds.map( id =>
    {return id === DEFAULT_BACKLOG.ID ? t( DEFAULT_BACKLOG.NAME ) : t( 'participant_not_found' );}
    );
  };

  const handleDuplicateParticipants = async ( duplicatedParticipants: string[] ) => {
    if ( duplicatedParticipants.length > 0 ) {
      try {
        const names = await getDuplicateParticipantsNames( duplicatedParticipants );
        setDuplicateParticipantsNames( names );
      } catch ( error ) {
        logger.error( 'Error getting duplicate participant names:', error );
        setDuplicateParticipantsNames( [] );
      }
    } else {
      setDuplicateParticipantsNames( [] );
    }
  };

  return { handleDuplicateParticipants };
};

const createInitializationHandlers = (
  refreshEvent: () => Promise<void>,
  setIsHighlighted: React.Dispatch<React.SetStateAction<boolean>>,
  setIsFirstRender: React.Dispatch<React.SetStateAction<boolean>>,
  engagementClicked: EventCommitment | undefined,
) => {
  return {
    initializeComponent: async () => {
      await refreshEvent();

      if ( !engagementClicked || ( !engagementClicked.eventTitle && !engagementClicked.eventId ) ) {
        setIsHighlighted( false );
      }

      setIsFirstRender( false );
    }
  };
};

const useEngagementModalLogic = (
  engagementClicked: EventCommitment | undefined,
  onEventManagerCreate: ( ( event: Event ) => void ) | undefined,
  onClose: () => void,
  isAdminOfEventManager: boolean | undefined,
  isViewMode: boolean | undefined,
  dateCLicked?: string | Date,
  userClickedId?: string,
) => {
  const dispatch = useDispatch();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );
  const connectedUser = useSelector( ( state: RootState ) => { return state.user.value; } );

  const engagementState = useEngagementState( engagementClicked );
  const fiveWState = use5WState();
  const modalState = useModalState();
  const duplicateState = useDuplicateParticipants();
  const [ errorMessage, setErrorMessage ] = useState<string | null>( null );
  const eventCompletionState = useEventCompletion();
  const pendingState = usePendingStates();

  const [ referenceEventDocuments, setReferenceEventDocuments ] = useState<EventDocument[]>( [] );
  const [ selectedSiteId ] = usePersistantState<string | undefined>( KEY.SELECTED_SITE_ID, undefined );
  const [ selectedSectorId ] = usePersistantState<string | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ customSettings, setCustomSettings ] = useState<Setting[]>( [] );
  const [ dynamicFields, setDynamicFields ] = useState<Record<string, string>>( {} );
  const [ eventManagersOptions, setEventManagersOptions ] = useState<GroupedOption<{ id: UUID, name: string }>[]>( [] );
  const [ eventActual, setEventActual ] = useState<EventData>();
  const [ dynamicFieldsActual, setDynamicFieldsActual ] = useState<Record<string, string>>( {} );
  const [ referenceEventNotes, setReferenceEventNotes ] = useState<Note[]>( [] );
  const [ initialReferenceEventId, setInitialReferenceEventId ] = useState<string | undefined>( undefined );
  const [ initialReferenceEventTitle, setInitialReferenceEventTitle ] = useState<string | undefined>( undefined );
  const [ eventManager, setEventManager ] = useState<EventManager | undefined>( undefined );
  const [ isActiveField, setIsActiveField ] = useState<boolean>( false );

  const saveDynamicFieldsRef = useRef<( () => Promise<void> ) | null>( null );

  const [ rawEventManagerId ] = usePersistantState<string | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );
  const readPersistedKey = ( key: string ): string | undefined => {
    try {
      const raw = localStorage.getItem( key );
      if ( raw == null ) { return undefined; }
      try {
        const parsed = JSON.parse( raw );
        return parsed == null ? undefined : String( parsed );
      } catch {
        return String( raw );
      }
    } catch {
      return undefined;
    }
  };

  const eventManagerId = rawEventManagerId ?? readPersistedKey( KEY.SELECTED_EVENT_MANAGER_ID );

  const [ storedDate ] = usePersistantState<string | undefined>( KEY.SELECTED_EVENT_MANAGER_COL_DATE, undefined );
  const daySelectedRaw: string = ( typeof dateCLicked === 'string' && dateCLicked.trim() !== '' )
    ? dateCLicked
    : ( typeof dateCLicked !== 'string' && dayjs( dateCLicked ).isValid() )
      ? dayjs( dateCLicked ).format( FORMAT_DATE_API )
      : ( storedDate ?? dayjs().format( FORMAT_DATE_API ) );

  const [ userIdSelectedRaw ] = usePersistantState<string | undefined>( KEY.SELECTED_EVENT_MANAGER_ROW_USER_ID, undefined );
  const persistedUser = userIdSelectedRaw ?? readPersistedKey( KEY.SELECTED_EVENT_MANAGER_ROW_USER_ID );
  const userIdSelected = userClickedId ?? persistedUser ?? connectedUser?.id ?? '';

  useEffect( () => {
    if ( eventManagerId != null && eventManagerId != '' ) {
      const refreshEventManager = async () => {
        let updatedEventManager: EventManager | null = null;
        if ( isViewMode ) {
          updatedEventManager = await VisualisationService.getEventManager( eventManagerId, visualisationToken, dispatch );
        } else {
          updatedEventManager = await EventManagerService.getEventManagerById( eventManagerId, token, dispatch );
        }
        setEventManager( updatedEventManager );
      };

      refreshEventManager();
    }
  }, [ eventManagerId ] );

  useEffect( () => {
    if ( eventManager ) {
      setIsActiveField( eventManager.activeField );
    }
  }, [ eventManager ] );

  return {
    ...engagementState,
    ...fiveWState,
    ...modalState,
    ...duplicateState,
    errorMessage, setErrorMessage,
    ...eventCompletionState,
    ...pendingState,

    referenceEventDocuments, setReferenceEventDocuments,
    selectedSiteId, selectedSectorId,
    customSettings, setCustomSettings,
    dynamicFields, setDynamicFields,
    eventManagersOptions, setEventManagersOptions,
    eventActual, setEventActual,
    dynamicFieldsActual, setDynamicFieldsActual,
    referenceEventNotes, setReferenceEventNotes,
    initialReferenceEventId, setInitialReferenceEventId,
    initialReferenceEventTitle, setInitialReferenceEventTitle,
    saveDynamicFieldsRef,

    eventManagerId,
    isActiveField,
    daySelectedRaw,
    userIdSelected,
    dispatch,
    token,
    visualisationToken,
    connectedUser,
    onEventManagerCreate,
    onClose,
    isAdminOfEventManager,
    engagementClicked
  };
};

const useEngagementModalHandlers = ( modalLogic: ReturnType<typeof useEngagementModalLogic>, isViewMode: boolean | undefined ) => {
  const {
    engagements, setEngagements, eventManagerId, token, visualisationToken, dispatch,
    setIsProcessing, setEventActual, setThemeSelected, setIsRecurrent, setIsHighlighted,
    setInitialReferenceEventId, setInitialReferenceEventTitle, setWhat, setWhen,
    setWho, setWhere, setWhy, setDynamicFieldsActual, setDynamicFields,
    setEventManagersOptions, engagementClicked, daySelectedRaw, userIdSelected,
    connectedUser, dynamicFields, eventActual, selectedSiteId, selectedSectorId,
    isFirstRender, setDuplicateParticipantsNames, setReferenceEventNotes,
    setReferenceEventDocuments, initialReferenceEventId, initialReferenceEventTitle,
    setIsEventCompletionModalOpen, setEventToArchive, onClose,
    setErrorMessage
  } = modalLogic;

  const { t, i18n } = useTranslation();

  const refreshThemes = async ( eventManagersId: string[] ) => {
    const updatedThemes: Theme[] = [];
    if ( isViewMode ) {
      updatedThemes.push( ...( await VisualisationService.getCommonThemesByEventManager( eventManagersId, visualisationToken, dispatch ) ) );
    } else {
      updatedThemes.push( ...( await ThemeService.getCommonThemesByEventManager( eventManagersId, token, dispatch ) ) );
    }

    modalLogic.setThemesOptions( updatedThemes );
  };

  const baseEngagementHandlers = createEngagementHandlers( setEngagements, eventManagerId, toastService, t, refreshThemes, daySelectedRaw );

  const addEngagement = () => {
    baseEngagementHandlers.addEngagement();
  };

  const updateEngagement = async ( index: number, key: keyof Engagement, value: string[] | string | Date ) => {
    const updatedEngagaments = engagements.map( ( engagement, engIndex ) => {
      const eventManagersId = ( engIndex === index && key === 'eventManagerIds' ) ? value as string[] : engagement.eventManagerIds;
      const participantsId = ( engIndex === index && key === 'participants' ) ? value as string[] : engagement.participants;
      const deliveryDate = dayjs( ( engIndex === index && key === 'deliveryDate' ) ? value as Date : engagement.deliveryDate ).format( FORMAT_DATE_API );
      return { ...engagement, eventManagerIds: eventManagersId, participants: participantsId, deliveryDate };
    } );

    baseEngagementHandlers.updateEngagement( index, key, value );
    setErrorMessage( await validationHelpers.checkIsValidEngagements( updatedEngagaments ) );
  };

  const removeEngagement = async ( index: number ) => {
    const updatedEngagaments = engagements.filter( ( engagement, engagementIndex ) => { return engagementIndex != index; } );

    baseEngagementHandlers.removeEngagement( index );
    setErrorMessage( await validationHelpers.checkIsValidEngagements( updatedEngagaments ) );
  };

  const refreshFunctions = createRefreshFunctions(
    setIsProcessing, setEventActual, setThemeSelected, setEngagements, setIsRecurrent,
    setIsHighlighted, setInitialReferenceEventId, setInitialReferenceEventTitle,
    setWhat, setWhen, setWho, setWhere, setWhy, setDynamicFieldsActual, setDynamicFields,
    setEventManagersOptions, refreshThemes, engagementClicked, eventManagerId,
    daySelectedRaw, userIdSelected, connectedUser, token, visualisationToken, dispatch,
    modalLogic.setSelectedEngagementIndex,
    isViewMode
  );

  const utilityFunctions = createUtilityFunctions(
    dynamicFields, setDynamicFields, eventActual, engagements, token, dispatch
  );

  const eventHandlers = createEventHandlers(
    setIsProcessing, setIsEventCompletionModalOpen, setEventToArchive,
    onClose, token, dispatch, toastService, t
  );

  const useEffectHandlers = createUseEffectHandlers(
    setIsProcessing, setReferenceEventNotes, setReferenceEventDocuments,
    eventActual, selectedSiteId, isFirstRender, token, visualisationToken, dispatch, isViewMode
  );

  const participantHandlers = createParticipantHandlers(
    engagements, setDuplicateParticipantsNames,
    token, dispatch, t
  );

  const initializationHandlers = createInitializationHandlers( refreshFunctions.refreshEvent, modalLogic.setIsHighlighted, modalLogic.setIsFirstRender, engagementClicked );

  const validationHelpers = createValidationHelpers(
    modalLogic.eventName, modalLogic.themesSelected, engagements, eventActual,
    modalLogic.isRecurrent, modalLogic.isHighlighted, modalLogic.who, modalLogic.where,
    modalLogic.when, modalLogic.what, modalLogic.why, modalLogic.customSettings,
    dynamicFields, modalLogic.dynamicFieldsActual, initialReferenceEventId, initialReferenceEventTitle,
    t, token, dispatch
  );

  const modalActionHandlers = createModalActionHandlers(
    engagementClicked, modalLogic.isProcessing, setIsProcessing, setEventToArchive,
    setIsEventCompletionModalOpen, token, dispatch, toastService, t, onClose,
    selectedSectorId, connectedUser, eventActual, modalLogic.who, modalLogic.what,
    modalLogic.where, modalLogic.when, i18n?.language ?? undefined
  );

  const referenceEventHandlers = createReferenceEventHandlers(
    setIsProcessing, setEventActual, setInitialReferenceEventId, setInitialReferenceEventTitle,
    modalLogic.setEventName, modalLogic.setThemeSelected, setEngagements, modalLogic.setIsRecurrent,
    setWhat, setWhen, setWho, setWhere, setWhy, setDynamicFieldsActual, setDynamicFields,
    modalLogic.eventName, modalLogic.themesSelected, engagements, modalLogic.isRecurrent,
    modalLogic.what, modalLogic.when, modalLogic.who, modalLogic.where, modalLogic.why,
    dynamicFields, token, visualisationToken, dispatch, toastService, t,
    isViewMode
  );

  return {
    refreshThemes,
    updateEngagement, addEngagement, removeEngagement,
    ...refreshFunctions,
    ...utilityFunctions,
    ...eventHandlers,
    ...useEffectHandlers,
    ...participantHandlers,
    ...initializationHandlers,
    validationHelpers,
    ...modalActionHandlers,
    ...referenceEventHandlers
  };
};

const useEngagementModalEventHandlers = (
  modalLogic: ReturnType<typeof useEngagementModalLogic>,
  handlers: ReturnType<typeof useEngagementModalHandlers>
) => {
  const {
    formValid, eventActual, engagements, eventName, duplicateParticipantsNames,
    themesSelected, isRecurrent, isHighlighted, what, when, who, where, why,
    initialReferenceEventId, initialReferenceEventTitle, isProcessing,
    setIsProcessing, onClose, onEventManagerCreate, token, dispatch
  } = modalLogic;

  const { t } = useTranslation();

  const { managedEngagementsOfEvent } = handlers;

  const handleModify = useCallback( async () => {
    if ( duplicateParticipantsNames.length > 0 ) {
      toastService.showError( t( 'translation:duplicate_participants_error' ) );
      return;
    }

    if ( formValid && eventActual ) {
      setIsProcessing( true );
      try {

        if ( eventActual.event.id ) {
          const updatedWhat = what != eventActual.event.w5What ? what : undefined;
          const updatedWhen = when != eventActual.event.w5When ? when : undefined;
          const updatedWho = who != eventActual.event.w5Who ? who : undefined;
          const updatedWhere = where != eventActual.event.w5Where ? where : undefined;
          const updatedWhy = why != eventActual.event.w5Why ? why : undefined;

          const eventToUpdate = createEventUpdatePayload(
            eventName, themesSelected || '', isHighlighted, isRecurrent,
            eventActual?.event?.referenceEventId, updatedWhat, updatedWhen, updatedWho, updatedWhere, updatedWhy
          );

          const response = await updateEventAPI( eventActual.event.id, eventToUpdate, token, dispatch );
          await managedEngagementsOfEvent( eventActual );

          if ( typeof handlers.saveDynamicFieldsToEvent === 'function' ) {
            await handlers.saveDynamicFieldsToEvent( undefined, true );
          }

          if ( response.status === 201 ) {
            toastService.showSuccess( t( 'translation:commitment_updated' ) );
          }
        }
        modalLogic.setReferenceEventNotes( [] );
        modalLogic.setReferenceEventDocuments( [] );
      } catch {
        toastService.showError( t( 'translation:error_updating_event' ) );
      } finally {
        setIsProcessing( false );
        onClose();
      }
    }
  }, [ duplicateParticipantsNames.length, formValid, eventActual, managedEngagementsOfEvent, eventName, themesSelected, isRecurrent, isHighlighted, what, when, who, where, why, setIsProcessing, t, token, dispatch, onClose, handlers.saveDynamicFieldsToEvent ] );

  const handleCreate = useCallback( async () => {
    if ( duplicateParticipantsNames.length > 0 ) {
      toastService.showError( t( 'translation:duplicate_participants_error' ) );
      return;
    }

    if ( formValid ) {
      setIsProcessing( true );
      try {
        const formattedEngagements = engagements.map( engagement => {
          const participants = ( engagement.participants || [] )
            .map( p => {return p ?? '';} )
            .map( p => {return String( p ).trim();} )
            .filter( p => {return p !== '';} );
          const eventManagerIds = ( engagement.eventManagerIds || [] )
            .map( id => {return id ?? '';} )
            .map( id => {return String( id ).trim();} )
            .filter( id => {return id !== '';} );

          return {
            eventManagerIds,
            deliveryDate: dayjs( engagement.deliveryDate ).format( FORMAT_DATE_API ),
            participants,
          };
        } );

        // Validate that no engagement has an empty participants list
        const invalidEngagementIndex = formattedEngagements.findIndex( fe => { return !Array.isArray( fe.participants ) || fe.participants.length === 0; } );
        if ( invalidEngagementIndex !== -1 ) {
          toastService.showError( t( 'translation:error_engagement_missing_participants' ) || 'Please add participants to all engagements' );
          setIsProcessing( false );
          return;
        }

        const newEvent: Event = {
          title: eventName || '',
          themeId: themesSelected || '',
          recurring: isRecurrent,
          highlighted: isHighlighted,
          referenceEventId: eventActual?.event?.referenceEventId || initialReferenceEventId,
          referenceEventTitle: eventActual?.event?.referenceEventTitle || initialReferenceEventTitle,
          w5What: what,
          w5When: when,
          w5Who: who,
          w5Where: where,
          w5Why: why,
          engagements: formattedEngagements
        };

        const typedWindow = window as WindowWithPending;
        const pendingNotes = typedWindow._pendingNotesForEvent?.getPendingNotes() || [];
        const pendingDocs = typedWindow._pendingDocumentsForEvent?.getPendingDocuments() || [];
        const formattedNotes = pendingNotes.map( noteContent => {return {
          value: noteContent,
          disable: false,
          predecessorId: null,
        };} );
        const newEventId = await EventService.createEventWithNotesAndDocuments(
          newEvent, formattedNotes, pendingDocs, token, dispatch
        );

        if ( newEventId ) {
          toastService.showSuccess( t( 'translation:commitment_create' ) );
          handlePostCreationCleanup( formattedNotes, pendingDocs );

          if ( pendingDocs.length > 0 ) {
            await loadDocumentsAfterCreation( newEventId );
          }

          try {
            const createdEvent = await EventService.getEventById( newEventId, token, dispatch );
            if ( createdEvent ) {
              modalLogic.setEventActual( createdEvent );
            }
          } catch {
            logger.error( 'Error while loading created event.' );
          }

          try {
            const eventToUpdate = createEventUpdatePayload(
              eventName || '',
              themesSelected || '',
              isHighlighted,
              isRecurrent,
              eventActual?.event?.referenceEventId || initialReferenceEventId,
              what,
              when,
              who,
              where,
              why
            );

            try {
              await updateEventAPI( newEventId, eventToUpdate, token, dispatch );
            } catch ( updateErr ) {
              logger.error( 'Error while calling updateEventAPI after creation:', updateErr );
            }

            if ( typeof handlers.saveDynamicFieldsToEvent === 'function' ) {
              await handlers.saveDynamicFieldsToEvent( newEventId );
            }
          } catch ( err ) {
            logger.error( 'Error while saving dynamic fields after creation:', err );
          }

          if ( onEventManagerCreate ) {
            onEventManagerCreate( newEvent );
          }
        } else {
          toastService.showError( t( 'translation:error_creating_event' ) );
        }
      } catch {
        toastService.showError( t( 'translation:error_creating_event' ) );
      } finally {
        setIsProcessing( false );
        onClose();
      }
    }
  }, [ duplicateParticipantsNames.length, formValid, engagements, eventName, themesSelected, isRecurrent, isHighlighted, eventActual, initialReferenceEventId, initialReferenceEventTitle, what, when, who, where, why, setIsProcessing, t, token, dispatch, onEventManagerCreate, onClose ] );

  const handleTabSelect = useCallback( ( index: number ) => {
    if ( isProcessing ) {return;}
    modalLogic.setSelectedPage( index );
  }, [ isProcessing, modalLogic.setSelectedPage ] );

  const handleNoteTrigger = useCallback( () => {
    if ( typeof window !== 'undefined' ) {
      const typedWindow = window as WindowWithPending & { _handleSendNote?: () => void };
      if ( typedWindow._handleSendNote ) {
        typedWindow._handleSendNote();
      }
    }
  }, [] );

  const handleDocumentsTrigger = useCallback( () => {
    if ( typeof window !== 'undefined' ) {
      const typedWindow = window as WindowWithPending & { _handleSaveDocuments?: () => void };
      if ( typedWindow._handleSaveDocuments ) {
        typedWindow._handleSaveDocuments();
      }
    }
  }, [] );

  const handleCustomUpdate = useCallback( async () => {
    try {
      if ( !eventActual?.event?.id ) {
        if ( modalLogic.hasPendingNote ) {
          handleNoteTrigger();
          await new Promise( resolve => {return setTimeout( resolve, 100 );} );
        }
        const createFunction = ( window as WindowWithPending & { _handleCreate?: () => void } )._handleCreate;
        if ( createFunction ) {
          createFunction();
        }
        return;
      }

      let actionPerformed = false;

      if ( modalLogic.hasPendingNote || modalLogic.hasPendingDocuments ) {
        setIsProcessing( true );
        if ( modalLogic.hasPendingNote ) {
          handleNoteTrigger();
          actionPerformed = true;
          await new Promise( resolve => {return setTimeout( resolve, 200 );} );
        }

        if ( modalLogic.hasPendingDocuments ) {
          handleDocumentsTrigger();
          actionPerformed = true;
          await new Promise( resolve => {return setTimeout( resolve, 200 );} );
        }
        setIsProcessing( false );
      }

      if ( !actionPerformed ) {
        const modifyFunction = ( window as WindowWithPending & { _handleModify?: () => void } )._handleModify;
        if ( modifyFunction ) {
          modifyFunction();
        }
      }
    } catch ( error ) {
      logger.error( 'Error in handleCustomUpdate:', error );
      setIsProcessing( false );
    }
  }, [ modalLogic.hasPendingNote, modalLogic.hasPendingDocuments, handleNoteTrigger, handleDocumentsTrigger, eventActual?.event?.id, setIsProcessing ] );

  return {
    handleModify,
    handleCreate,
    handleTabSelect,
    handleNoteTrigger,
    handleDocumentsTrigger,
    handleCustomUpdate
  };
};

const EngagementsSectionWrapper: React.FC<{
  modalLogic: ReturnType<typeof useEngagementModalLogic>;
  handlers: ReturnType<typeof useEngagementModalHandlers>;
  isViewMode?: boolean;
  engagementClicked?: EventCommitment;
  isAdminOfEventManager?: boolean;
}> = ( { modalLogic, handlers, isViewMode, engagementClicked, isAdminOfEventManager } ) => {

  const { t } = useTranslation();

  const handleReferenceEventClick = useCallback( async ( id: string ) => {
    try {
      const isCreating = !modalLogic.eventActual?.event?.id || modalLogic.eventActual.event.id === '';

      if ( isCreating ) {
        await handlers.loadReferenceEventForCreation( id );
      } else {
        await handlers.linkReferenceEventForModification( id );
      }
    } catch ( error ) {
      logger.error( 'Error loading reference event:', error );
      toastService.showError( t( 'translation:error_loading_reference_event' ) );
    }
  }, [ modalLogic.eventActual?.event?.id, handlers, t ] );

  const handleReferenceEventLinkClick = useCallback( async ( id: string ) => {
    try {
      await handlers.linkReferenceEventForModification( id );
    } catch ( error ) {
      logger.error( 'Error loading reference event:', error );
    }
  }, [ handlers ] );

  return (
    <>
      <EngagementsSection
        isViewMode={ isViewMode }
        isActiveField={ modalLogic.isActiveField }
        setWhat={ modalLogic.setWhat }
        setWhen={ modalLogic.setWhen }
        setWhere={ modalLogic.setWhere }
        setWho={ modalLogic.setWho }
        setWhy={ modalLogic.setWhy }
        what={ modalLogic.what }
        when={ modalLogic.when }
        where={ modalLogic.where }
        who={ modalLogic.who }
        why={ modalLogic.why }
        dynamicFields={ modalLogic.dynamicFields }
        updateDynamicField={ handlers.updateDynamicField }
        customSettings={ modalLogic.customSettings }
        eventManagerOptions={ modalLogic.eventManagersOptions }
        engagements={ modalLogic.engagements }
        themesOptions={ modalLogic.themesOptions }
        isArchived={ engagementClicked?.archived }
        themesSelected={ modalLogic.themesSelected }
        eventName={ modalLogic.eventName }
        isRecurrent={ modalLogic.isRecurrent }
        setIsRecurrent={ modalLogic.setIsRecurrent }
        isHighlighted={ modalLogic.isHighlighted }
        setIsHighlighted={ modalLogic.setIsHighlighted }
        updateEngagement={ handlers.updateEngagement }
        removeEngagement={ handlers.removeEngagement }
        setThemeSelected={ modalLogic.setThemeSelected }
        setEventName={ modalLogic.setEventName }
        addEngagement={ handlers.addEngagement }
        eventManagerId={ modalLogic.eventManagerId }
        referenceEventId={ modalLogic.eventActual?.event?.referenceEventId }
        referenceEventTitle={ modalLogic.eventActual?.event?.referenceEventTitle }
        onReferenceEventClick={ handleReferenceEventClick }
        onReferenceEventLinkClick={ handleReferenceEventLinkClick }
        isAdminOfEventManager={isAdminOfEventManager ?? false}
        currentEventId={modalLogic.eventActual?.event?.id}
        errorMessage={modalLogic.errorMessage}
      />
    </>
  );
};

const NotesSectionWrapper: React.FC<{
  modalLogic: ReturnType<typeof useEngagementModalLogic>;
  isViewMode?: boolean;
  engagementClicked?: EventCommitment;
  setCurrentNotes: React.Dispatch<React.SetStateAction<Note[]>>;
  isAdminOfEventManager?: boolean;
}> = ( { modalLogic, isViewMode, engagementClicked, isAdminOfEventManager, setCurrentNotes } ) => {
  const getSiteId = useCallback( () => {
    const siteIdRaw = modalLogic.selectedSiteId ?? '';
    if ( !siteIdRaw ) { return ''; }
    const raw = String( siteIdRaw ).trim();
    if ( raw.length === 0 ) { return ''; }

    const firstChar = raw[ 0 ];
    if ( firstChar === '{' || firstChar === '[' || firstChar === '"' ) {
      try {
        const parsed = JSON.parse( raw );
        return parsed == null ? '' : String( parsed );
      } catch {
        // malformed JSON, fall back to raw without surrounding quotes
        return raw.replace( /^"|"$/g, '' );
      }
    }

    return raw;
  }, [ modalLogic.selectedSiteId ] );

  const isParticipant = useMemo( () => {
    return modalLogic.eventActual?.engagements
      ? modalLogic.eventActual.engagements.some( engagement => {
        return Array.isArray( engagement.participants ) &&
        engagement.participants.some( p => {
          if ( typeof p === 'object' && p !== null && 'id' in p ) {
            return p === modalLogic.connectedUser?.id;
          }
          return p === modalLogic.connectedUser?.id;
        } );
      } )
      : true;
  }, [ modalLogic.eventActual?.engagements, modalLogic.connectedUser?.id ] );

  return (
    <NotesSection
      isViewMode={ isViewMode }
      selectedPage={modalLogic.selectedPage}
      isArchived={engagementClicked?.archived}
      dispatch={modalLogic.dispatch}
      eventId={modalLogic.eventActual?.event?.id || ''}
      siteId={ getSiteId() }
      referenceEventId={modalLogic.eventActual?.event?.referenceEventId}
      referenceEventTitle={modalLogic.eventActual?.event?.referenceEventTitle}
      referenceEventNotes={modalLogic.referenceEventNotes}
      isCreationMode={!modalLogic.eventActual?.event?.id}
      isAdminOfEventManager={isAdminOfEventManager ?? false}
      isParticipant={ isParticipant }
      setParentCurrentNotes={ setCurrentNotes }
      onPendingNoteChange={modalLogic.setHasPendingNote}
      onTriggerNoteSend={() => {return true;}}
    />
  );
};

const DocumentsSectionWrapper: React.FC<{
  modalLogic: ReturnType<typeof useEngagementModalLogic>;
  isViewMode?: boolean;
  engagementClicked?: EventCommitment;
  isAdminOfEventManager?: boolean;
  setCurrentDocuments: React.Dispatch<React.SetStateAction<EventDocument[]>>;
}> = ( { modalLogic, isViewMode, engagementClicked, isAdminOfEventManager, setCurrentDocuments } ) => {
  const isParticipant = useMemo( () => {
    return modalLogic.eventActual?.engagements
      ? modalLogic.eventActual.engagements.some( engagement => {
        return Array.isArray( engagement.participants ) &&
        engagement.participants.some( p => {
          if ( typeof p === 'object' && p !== null && 'id' in p ) {
            return p === modalLogic.connectedUser?.id;
          }
          return p === modalLogic.connectedUser?.id;
        } );
      } )
      : true;
  }, [ modalLogic.eventActual?.engagements, modalLogic.connectedUser?.id ] );

  return (
    <DocumentsSection
      isViewMode={ isViewMode }
      isArchived={engagementClicked?.archived}
      eventId={modalLogic.eventActual?.event?.id || ''}
      referenceEventId={modalLogic.eventActual?.event?.referenceEventId}
      referenceEventTitle={modalLogic.eventActual?.event?.referenceEventTitle}
      referenceEventDocuments={modalLogic.referenceEventDocuments}
      isCreationMode={!modalLogic.eventActual?.event?.id}
      isAdminOfEventManager={isAdminOfEventManager ?? false}
      isParticipant={ isParticipant }
      setParentCurrentDocuments={setCurrentDocuments}
      onPendingDocumentChange={modalLogic.setHasPendingDocuments}
      onTriggerDocumentsSave={() => {return true;}}
    />
  );
};

const EngagementModal: React.FC<EventModalProps> = ( {
  onEventManagerCreate,
  onClose,
  isViewMode,
  engagementClicked,
  isAdminOfEventManager,
  dateCLicked,
  userClickedId
} ) => {

  const { t } = useTranslation();

  const [ currentDocuments, setCurrentDocuments ] = useState<EventDocument[]>( [] );
  const [ currentNotes, setCurrentNotes ] = useState<Note[]>( [] );
  const currentUserId = useSelector( ( state: RootState ) => {return state.user?.value?.id;} );

  const modalLogic = useEngagementModalLogic( engagementClicked, onEventManagerCreate, onClose, isAdminOfEventManager, isViewMode, dateCLicked, userClickedId );
  const handlers = useEngagementModalHandlers( modalLogic, isViewMode );
  const eventHandlers = useEngagementModalEventHandlers( modalLogic, handlers );

  useEffect( () => {
    try {
      if ( typeof window !== 'undefined' && window.location.search && window.location.search.length > 0 ) {
        // Remove query parameters to prevent repeated redirection on refresh
        const newUrl = window.location.pathname;
        window.history.replaceState( null, document.title, newUrl );
      }
    } catch ( err ) {
      logger.error( 'Error clearing URL query params:', err );
    }
  }, [] );

  const pages: TabItem[] = [
    {
      id: 'Principal',
      name: t( 'translation:principal' )
    },
    {
      id: 'Note',
      name: t( 'translation:note' ) + ' (' + ( currentNotes?.length ?? 0 ) + ')'
    },
    {
      id: 'Document',
      name: t( 'translation:document' ) + ' (' + ( currentDocuments?.length ?? 0 ) + ')'
    }
  ];

  useEngagementInitializationEffect( handlers.refreshEvent, engagementClicked, modalLogic.setIsHighlighted, modalLogic.setIsFirstRender );
  useEngagementSettingsFetchEffect( modalLogic.isFirstRender, modalLogic.selectedSiteId, modalLogic.setIsProcessing, modalLogic.setCustomSettings, modalLogic.token, modalLogic.visualisationToken, modalLogic.dispatch, modalLogic.setDynamicFields, isViewMode ?? false );

  useEffect( () => {
    if ( Array.isArray( modalLogic.themesOptions ) && modalLogic.themesOptions.length > 0 ) {
      modalLogic.setThemeSelected( modalLogic.themesOptions.find( theme => {return modalLogic.themesSelected === theme.id;} )?.id ?? '' );
    }
  }, [ modalLogic.themesOptions ] );

  useEffect( () => {
    if ( !modalLogic.isFirstRender ) {
      const requirement = handlers.validationHelpers.validateFormRequirements();
      const hasChanges = handlers.validationHelpers.checkFormChanges();
      modalLogic.setFormValid( requirement && hasChanges && !modalLogic.errorMessage );
      modalLogic.setFormChanged( hasChanges );
    }
  }, [
    modalLogic.eventName, modalLogic.themesSelected, modalLogic.isRecurrent, modalLogic.isHighlighted,
    modalLogic.engagements, modalLogic.who, modalLogic.where, modalLogic.when, modalLogic.what,
    modalLogic.why, modalLogic.dynamicFields, modalLogic.eventActual, modalLogic.isFirstRender,
    modalLogic.errorMessage
  ] );

  useEffect( () => {
    if ( modalLogic.saveDynamicFieldsRef ) {
      modalLogic.saveDynamicFieldsRef.current = handlers.saveDynamicFieldsToEvent;
    }
  }, [ modalLogic.saveDynamicFieldsRef, modalLogic.dynamicFields, modalLogic.eventActual?.event?.id ] );

  useEffect( () => {
    ( window as WindowWithPending & { _handleModify?: () => void } )._handleModify = eventHandlers.handleModify;
  }, [ eventHandlers.handleModify ] );

  useEffect( () => {
    ( window as WindowWithPending & { _handleCreate?: () => void } )._handleCreate = eventHandlers.handleCreate;
  }, [ eventHandlers.handleCreate ] );

  handlers.useReferenceDataEffect();

  const isGlobalLoading = modalLogic.isProcessing;

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      isLoading={isGlobalLoading}
      loadingMessage={t( 'translation:saving_data' )}
      noConfirmationModal={ !modalLogic.formChanged || ( !modalLogic.themesOptions || modalLogic.themesOptions.length === 0 ) }
      icon={
        engagementClicked ?
          <EditBoxIcon className="bg-orange-200 p-2 rounded-md" size={10} />
          :
          <PlusIcon className="bg-green-200 p-2 rounded-md" size={10} />
      }
      footer={
        <>
          {
            !isViewMode && (
              <BottomModal
                tableId={engagementClicked ? engagementClicked.eventId : ''}
                showButtons={true}
                formValid={
                  modalLogic.eventActual?.event?.id
                    ? ( ( modalLogic.formValid || modalLogic.hasPendingNote || modalLogic.hasPendingDocuments ) && !isGlobalLoading )
                    : ( modalLogic.formValid && !isGlobalLoading )
                }
                onCreate={eventHandlers.handleCustomUpdate}
                onUpdate={!engagementClicked?.archived ? eventHandlers.handleCustomUpdate : undefined}
                extraButtons={
                  engagementClicked?.eventId ?
                    [
                      {
                        label: t( 'translation:create_problem_resolution' ),
                        icon: <PlusIcon className='h-5 w-5 text-white' />,
                        onClick: handlers.handleLaunchResolution,
                        disable: isGlobalLoading || !modalLogic.eventActual?.event?.id || !modalLogic.selectedSiteId,
                        textColor: 'text-white',
                        bgColor: 'bg-custom-green hover:bg-custom-green',
                        borderColor: 'border-transparent',
                        hoverBorderColor: 'hover:border-green-600',
                      },
                      {
                        label: t( 'translation:download_pdf' ),
                        icon: <DownloadIcon className='h-5 w-5 text-white' />,
                        onClick: handlers.handleDownloadPDF,
                        disable: isGlobalLoading || !modalLogic.eventActual?.event?.id,
                        textColor: 'text-white',
                        bgColor: 'bg-blue-600 hover:bg-blue-700',
                        borderColor: 'border-transparent',
                        hoverBorderColor: 'hover:border-blue-800',
                      },
                      ...(
                        (
                          isAdminOfEventManager ||
                          engagementClicked.participants.map( user => { return user.id; } ).includes( currentUserId ?? '' ) ||
                          ( engagementClicked.participants.map( user => { return user.id; } ).includes( DEFAULT_BACKLOG.ID ) && engagementClicked.participants.length === 1 )
                        ) ?
                          [
                            {
                              label: engagementClicked.archived ?  t( 'translation:unarchive' ) : t( 'translation:archive' ),
                              icon: <ArchiveBoxIcon className='h-5 w-5' />,
                              onClick: handlers.handleArchived,
                              disable: modalLogic.isProcessing,
                              textColor: 'text-purple-700',
                              bgColor: 'bg-purple-300',
                              borderColor: 'border-transparent',
                              hoverBorderColor: 'hover:border-purple-700',
                            }
                          ]
                          :
                          []
                      ),
                      ...(
                        (
                          !engagementClicked.archived &&
                            (
                              isAdminOfEventManager ||
                              engagementClicked.participants.map( user => { return user.id; } ).includes( currentUserId ?? '' ) ||
                              ( engagementClicked.participants.map( user => { return user.id; } ).includes( DEFAULT_BACKLOG.ID ) && engagementClicked.participants.length === 1 )
                            )
                        ) ?
                          [
                            {
                              label: engagementClicked.completed ? t( 'translation:not_finished' ) : t( 'translation:finished' ),
                              icon: <CheckIcon className='h-5 w-5'/>,
                              onClick: handlers.handleCompleted,
                              disable: modalLogic.isProcessing,
                              textColor: 'text-green-600',
                              bgColor: 'bg-white hover:bg-green-200 hover:shadow-none',
                              borderColor: 'border-transparent',
                              hoverBorderColor: 'hover:border-green-400',
                            }
                          ]
                          :
                          []
                      ),
                    ]
                    :
                    undefined
                }
              />
            )
          }
        </>
      }
      title={
        engagementClicked
          ? `${t( 'translation:editing_event' )}`
          : t( 'translation:creating_event' )
      }
    >
      <div className='px-5 rounded-md font-texte overflow-y-auto'>
        <Tabs items={pages} activeItem={modalLogic.selectedPage} onItemClick={eventHandlers.handleTabSelect}/>
        <div className='bg-white-base shadow-md border rounded-b-xl mb-2'>
          {
            modalLogic.selectedPage === 0 &&
            <EngagementsSectionWrapper
              modalLogic={modalLogic}
              handlers={handlers}
              isViewMode={isViewMode}
              engagementClicked={engagementClicked}
              isAdminOfEventManager={isAdminOfEventManager}
            />
          }
          <div style={{ display: modalLogic.selectedPage === 1 ? 'block' : 'none' }}>
            <NotesSectionWrapper
              modalLogic={modalLogic}
              isViewMode={isViewMode}
              engagementClicked={engagementClicked}
              isAdminOfEventManager={isAdminOfEventManager}
              setCurrentNotes={ setCurrentNotes }
            />
          </div>

          <div style={{ display: modalLogic.selectedPage === 2 ? 'block' : 'none' }}>
            <DocumentsSectionWrapper
              modalLogic={modalLogic}
              isViewMode={isViewMode}
              engagementClicked={engagementClicked}
              isAdminOfEventManager={isAdminOfEventManager}
              setCurrentDocuments={setCurrentDocuments}
            />
          </div>
        </div>
      </div>


      {modalLogic.eventActual && (
        <div className="mt-4 text-sm text-gray-500 flex justify-end gap-4 items-center pr-5">
          <span className="flex items-center gap-1"><UserIcon className="h-4 w-4" />
            { modalLogic.eventActual.event.authorFullname === DEFAULT_BACKLOG.NAME ? t( DEFAULT_BACKLOG.NAME ) : modalLogic.eventActual.event.authorFullname }
          </span>
          <span className="flex items-center gap-1"><CalendarIcon className="h-4 w-4" />
            { dayjs( modalLogic.eventActual.event.createdDate ).format( 'L' ) }
          </span>
        </div>
      )}

      <EventCompletionModal
        isVisible={modalLogic.isEventCompletionModalOpen}
        eventDroped={modalLogic.eventToArchive}
        onClose={handlers.handleEventCompletionClose}
        onConfirmNo={() => { return handlers.handleEventCompletionConfirmNo( modalLogic.eventToArchive ); }}
        onConfirmYes={() => { return handlers.handleEventCompletionConfirmYes( modalLogic.eventToArchive ); }}
      />
    </BaseModal>
  );
};

export default EngagementModal;

interface PendingNotesHandler {
  getPendingNotes: () => string[];
  clearPendingNotes: () => void;
}

interface PendingDocumentsHandler {
  getPendingDocuments: () => File[];
  clearPendingDocuments: () => void;
  loadDocumentsAfterCreation: ( newEventId: string ) => Promise<void>;
}

interface WindowWithPending extends Window {
  _pendingNotesForEvent?: PendingNotesHandler;
  _pendingDocumentsForEvent?: PendingDocumentsHandler;
  _currentDraftNote?: string;
  _temporaryNotes?: string[];
  _pendingFieldSettings?: Record<string, string>;
  _postFieldValues?: ( eventId: string, values: Record<string, string> ) => Promise<void>;
}